import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  FileText, Users, Clock, CheckCircle, AlertTriangle, Plus, Search,
  TrendingUp, FileCheck, UserCheck, Send, Package, XCircle, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComplianceDashboard = ({ agent, onNavigate, isAdmin = false }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const url = (isAdmin || !agent || agent.id === 'admin')
        ? `${API_URL}/api/compliance/dashboard/stats`
        : `${API_URL}/api/compliance/dashboard/stats?advisor_id=${agent.id}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  }, [agent, isAdmin]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statusConfig = {
    draft: { color: 'bg-gray-100 text-gray-700', icon: FileText },
    in_progress: { color: 'bg-blue-100 text-blue-700', icon: Clock },
    pending_client_signature: { color: 'bg-yellow-100 text-yellow-700', icon: UserCheck },
    pending_supervisor_review: { color: 'bg-orange-100 text-orange-700', icon: FileCheck },
    submitted_to_insurer: { color: 'bg-purple-100 text-purple-700', icon: Send },
    underwriting: { color: 'bg-indigo-100 text-indigo-700', icon: Search },
    approved: { color: 'bg-teal-100 text-teal-700', icon: CheckCircle },
    issued: { color: 'bg-cyan-100 text-cyan-700', icon: Package },
    delivered: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
    closed: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    compliance_hold: { color: 'bg-red-100 text-red-700', icon: XCircle },
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      in_progress: 'In Progress',
      pending_client_signature: 'Pending Client Signature',
      pending_supervisor_review: 'Pending Supervisor Review',
      submitted_to_insurer: 'Submitted to Insurer',
      underwriting: 'Underwriting',
      approved: 'Approved',
      issued: 'Issued',
      delivered: 'Delivered',
      closed: 'Closed',
      compliance_hold: 'Compliance Hold',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#003D5C]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-xl sm:text-2xl font-bold text-[#003D5C]">Compliance Dashboard</h1>
          <p className="text-sm text-gray-500">Life Insurance Compliance File Builder</p>
        </div>
        <Button
          onClick={() => onNavigate('new-case')}
          className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C] w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Insurance Case
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs">Active Files</p>
                <p className="text-xl sm:text-2xl font-bold">{stats?.total_active || 0}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-xs">Pending Signature</p>
                <p className="text-xl sm:text-2xl font-bold">{stats?.pending_client_signature || 0}</p>
              </div>
              <UserCheck className="h-8 w-8 text-yellow-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-xs">Supervisor Review</p>
                <p className="text-xl sm:text-2xl font-bold">{stats?.pending_supervisor_review || 0}</p>
              </div>
              <FileCheck className="h-8 w-8 text-orange-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-xs">Underwriting</p>
                <p className="text-xl sm:text-2xl font-bold">{stats?.underwriting || 0}</p>
              </div>
              <Search className="h-8 w-8 text-indigo-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-100 text-xs">Pending Delivery</p>
                <p className="text-xl sm:text-2xl font-bold">{stats?.pending_delivery || 0}</p>
              </div>
              <Package className="h-8 w-8 text-cyan-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-xs">Missing Docs</p>
                <p className="text-xl sm:text-2xl font-bold">{stats?.missing_documents || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Files by Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(stats?.status_counts || {}).map(([status, count]) => {
              const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-700', icon: FileText };
              const Icon = config.icon;
              return (
                <div 
                  key={status}
                  className={`p-3 rounded-lg ${config.color} cursor-pointer hover:opacity-80 transition-opacity`}
                  onClick={() => onNavigate('cases', { status })}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="text-xs font-medium">{getStatusLabel(status)}</span>
                  </div>
                  <p className="text-xl sm:text-2xl font-bold mt-1">{count}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent Cases */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Recent Files</CardTitle>
          <Button variant="outline" size="sm" onClick={() => onNavigate('cases')}>
            View All
          </Button>
        </CardHeader>
        <CardContent>
          {stats?.recent_cases?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_cases.map(caseItem => {
                const config = statusConfig[caseItem.status] || { color: 'bg-gray-100 text-gray-700' };
                return (
                  <div 
                    key={caseItem.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => onNavigate('case-detail', { caseId: caseItem.id })}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#003D5C] rounded-full flex items-center justify-center text-white font-bold">
                        {caseItem.client_name?.charAt(0) || 'C'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{caseItem.client_name}</p>
                        <p className="text-sm text-gray-500">{caseItem.case_number} • {caseItem.carrier}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-sm font-medium">${caseItem.coverage_amount?.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{caseItem.product_type}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                        {getStatusLabel(caseItem.status)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No cases yet</p>
              <Button 
                onClick={() => onNavigate('new-case')}
                className="mt-4 bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]"
              >
                Create Your First Case
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => onNavigate('clients')}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Manage Clients</h3>
              <p className="text-sm text-gray-500">View and edit client profiles</p>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => onNavigate('cases')}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">All Files</h3>
              <p className="text-sm text-gray-500">View all insurance cases</p>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => onNavigate('new-case')}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
              <Plus className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">New File</h3>
              <p className="text-sm text-gray-500">Start a new insurance file</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer Notice */}
      <div className="text-center text-xs text-gray-500 py-4 border-t">
        This system is designed to support documentation and compliance. Final suitability determination must be reviewed and approved by a licensed insurance advisor.
      </div>
    </div>
  );
};

export default ComplianceDashboard;
